/**
 * @file project-mixin.ts
 *
 * Project and session lifecycle for as-app: state ownership, the appData API,
 * before-save hooks, autosave capture, project load/save, and the session
 * visibility/freeze/thaw handling. On connect it creates an initial project and
 * registers the listeners that drive all of it.
 *
 * Requires: ModalMixinMembers, ErrorMixinMembers, InitQueueMixinMembers
 * Exposes: unsavedChanges, isSaving, pendingAction, currentProject, frozenAt,
 *          sessionSnapshotNeeded, autoSaveDebounceTimer, unsavedSessionProject,
 *          appData (Proxy: patch/defaults/revert/patchDefaults/subscribe/subscribeAll/derive),
 *          setMigrations(migrations), loadProject(file). customData/setCustomData/
 *          setCustomDataDefaults/revertCustomData/reapplyCustomDataDefaults/
 *          customDataSubscribe/projectData are deprecated aliases — see appData.
 */
import {
  createNewProject,
  markProjectSaved,
  serializeProject,
  deserializeProject,
  currentProjectData,
  applyMigrations,
  setMigrations,
  appData as appDataNs,
  type Project,
  type MigrationMap,
  type DeriveAppDataSpec,
} from '../../../data/projectManagement.ts';
import { internalData } from '../../../store/store.ts';
import { devlog, registerListeners, unregisterListeners } from '../../../util/utils.ts';
import { saveFileAs } from '../../../features/host/fileSave.ts';
import { getProjectsForApp, deleteProject } from '../../../features/db/db.ts';
import { cloneDeep as _cloneDeep, merge as _merge } from 'lodash';
import { isAppBusy } from './busy-mixin.ts';
import {
  sendEv,
  AS_CONFIGURATOR_EVENTS,
  isRealProjectSave,
  type ProjectSaveReason,
  type ProjectSavedDetail,
  type ProjectLoadDetail,
} from '../../../data/events.ts';
import { _setSysData } from '../../../store/sysDataStore.ts';
import { asConfigurator, asHeader } from '../../../util/singletons.ts';
import type { AsModalSaveProject } from '../../modals/as-modal-save-project.ts';
import { type Constructor } from './mixin-types-utils.ts';
import type { ModalMixinMembers } from './modal-mixin.ts';
import type { ErrorMixinMembers } from './error-mixin.ts';
import type { InitQueueMixinMembers } from './init-queue-mixin.ts';

/** Safety-net delay for releasing init dirty-suppression if a configurator never
 *  reports ready (e.g. init failure), so the app can't stay permanently clean. */
const INIT_RELEASE_FALLBACK_MS = 10000;

/** How long after the last change the capture runs. Every change restarts it,
 *  so a run of edits settles into one capture instead of one per edit — the
 *  capture is not free (before-save hooks plus a storeSession round-trip). */
const AUTOSAVE_DEBOUNCE_MS = 30_000;

/** Used instead of the full debounce when the app was busy at capture time:
 *  we're only waiting for the operation in flight to finish, so look again
 *  soon rather than pushing the capture another 30s out each time. */
const AUTOSAVE_BUSY_RETRY_MS = 2_000;

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// The session id whose storeSession() failed. _captureProjectState skips the
// capture while that session is still the live one: a session that can't be
// captured (expired, or the token isn't authorized for it) would otherwise be
// retried on every edit, each attempt logging an SDK console error. A reinit
// brings a new session id, so it gets its own chance without any reset.
// Module-level, so it is scoped to the page load rather than to one element.
let sessionCaptureBrokenFor: string | null = null;

// Sleep detection is a single page-wide interval, not one per element.
let lastSleepTick = Date.now();
let sleepDetectionInterval: number | null = null;

// One-time (per page) warning when a deprecated customData method is called —
// same pattern as as-file-input's warnFileSelectedListenerDeprecated.
const _warnedDeprecatedApi = new Set<string>();
function warnDeprecated(oldApi: string, newApi: string): void {
  if (_warnedDeprecatedApi.has(oldApi)) return;
  _warnedDeprecatedApi.add(oldApi);
  console.warn(`[as-app] '${oldApi}' is deprecated — use '${newApi}' instead.`);
}

/** Members this mixin reads off the finished element. AsApp supplies the first
 *  two; clearHistory comes from HistoryMixin, which sits further out in the
 *  chain, so neither can be a constraint on the base. */
interface ProjectHost {
  appId: string;
  version: string;
  clearHistory(): void;
}

/**
 * Ergonomic wrapper around appData reads/writes, grouping the
 * patch/defaults/revert operations that used to be flat methods on the app
 * element (setCustomData, setCustomDataDefaults, revertCustomData,
 * reapplyCustomDataDefaults, customDataSubscribe — now deprecated aliases,
 * as is the bare `customData` getter and the earlier `projectData` name for
 * this same wrapper), plus subscribeKey/derive (both already exist on
 * currentProjectData itself — see projectManagement.ts — just re-surfaced
 * here so apps consuming `as-app.appData` don't need a separate import to
 * reach them).
 *
 * Generic over an app's own seed-shape interface (default `Record<string, any>`
 * for untyped callers) — apps with a typed seed interface (e.g. Outlines2's
 * `ProjectSeeds`) can cast `document.querySelector('as-app').appData as
 * AppDataAPI<MySeeds>` at their own call site to get typed property access
 * and typed subscribeKey, without AsApp itself needing to be generic.
 */
export type AppDataAPI<TSeeds extends Record<string, any> = Record<string, any>> = TSeeds & {
  /** Merge a patch into appData in one call — one key or many. Top-level keys
   *  only: a key you pass replaces whatever was there. Set silent=true to skip
   *  dirty-marking/history. Same method as the module-level `appData.patch`. */
  patch(patch: Partial<TSeeds> & Record<string, unknown>, silent?: boolean): void;
  /** Replace the whole of appData, dropping any key not in `next`. What a
   *  project load does. Set silent=true to skip dirty-marking/history. */
  set(next: Partial<TSeeds> & Record<string, unknown>, silent?: boolean): void;
  /** Stored appData defaults. Setting replaces them and deep-merges missing keys into appData (existing values win). */
  defaults: Partial<TSeeds>;
  /** Wipe appData back to the stored defaults. */
  revert(): void;
  /** Re-merge the stored defaults into current appData (existing values win) —
   *  backfills any seed missing from a wholesale replace (e.g. an older saved project blob). */
  patchDefaults(): void;
  /** React to one key, several, or an alias object. Does NOT fire immediately.
   *  The same method as the module-level `appData.subscribe` — this object is
   *  that one plus the project-lifecycle helpers below. */
  subscribe: typeof appDataNs.subscribe;
  /** React to any appData change, with the whole object. Does NOT fire
   *  immediately. */
  subscribeAll(cb: (data: TSeeds) => void): () => void;
  /** Computed store built from appData — see `currentProjectData.derive` in
   *  projectManagement.ts for the full behavior. */
  derive<T extends Record<string, DeriveAppDataSpec<any>>>(spec: T): {
    readonly [K in keyof T]: ReturnType<T[K]['get']>;
  } & {
    subscribeKey(key: keyof T, cb: (value: any) => void): () => void;
  };
  /** console.log a plain, shallow-copied snapshot of appData — `appData`
   *  itself is a Proxy wrapping another Proxy, so devtools prints it as
   *  `Proxy(Proxy(Object))` and won't show contents inline. This gives a clean
   *  one-shot printout without changing what `appData` itself is. */
  print(): void;
};

/** @deprecated use `AppDataAPI` instead — renamed for consistency with
 *  `currentProjectData.appData`. Same shape. */
export type ProjectDataAPI<TSeeds extends Record<string, any> = Record<string, any>> = AppDataAPI<TSeeds>;

/** Opaque handle returned by addBeforeSaveHook. */
export type BeforeSaveHookHandle = symbol;

/** Why the before-save hooks are running: a real save, or the debounced
 *  in-memory autosave capture. Same vocabulary as `as-after-project-saved`'s
 *  detail.reason — see ProjectSaveReason in data/events.ts. */
export type BeforeSaveReason = ProjectSaveReason;

/** What every before-save hook is handed. An object (not a bare string) so
 *  more context can be added later without touching existing hooks. */
export interface BeforeSaveHookContext {
  reason: BeforeSaveReason;
}

export type BeforeSaveHook = (ctx: BeforeSaveHookContext) => void | Promise<void>;

/** A hook that threw, paired with the hook itself. Hooks are anonymous
 *  callbacks with no name to report, so the function travels with the error —
 *  logging it prints a devtools link straight to the offending source. */
export interface BeforeSaveHookFailure {
  fn: BeforeSaveHook;
  error: unknown;
}

/** Surface added by ProjectMixin. */
export interface ProjectMixinMembers {
  unsavedChanges: boolean;
  /**
   * Clears unsavedChanges through the post-load "settle" window. New / Restore /
   * cold boot leave the project unmodified by definition, but the async configure
   * they trigger emits as-input-change *after* the synchronous clear, which would
   * re-dirty a naive one-shot reset. Clears now, after the microtask flush, and
   * once more after the next configurator settle.
   */
  markPristine(): void;
  isSaving: boolean;
  /**
   * Tracks a deferred action that needs to run after a save completes (or be
   * cancelled if the user aborts the save). `null` means no action is pending.
   *
   * - `'new-session'`    — user clicked New while there were unsaved changes;
   *                        confirmed-save leads to _createAndSetNewProject.
   * - `'home'`           — user clicked Home while there were unsaved changes;
   *                        confirmed-save leads to redirectToStorefront.
   * - `'save-and-close'` — user clicked "Save and close" (e.g. GrabCAD modal);
   *                        confirmed-save dispatches as-close-app on window.
   * - `'transfer'`       — user started a mesh transfer while there were
   *                        unsaved changes; confirmed-save leads back into
   *                        prepareTransferMesh.
   *
   * Set by the action initiator; cleared either by the as-after-project-saved listener
   * (success path) or, on cancel, by cancel-save-project / as-intrn-cancel-exit-app
   * (modal-driven paths) or the as-project-save-cancelled listener ('save-and-close'
   * only — its native GrabCAD save dialog has no modal of ours to route a cancel
   * through). See docs/as-app.md § pendingAction state machine.
   */
  pendingAction: 'new-session' | 'home' | 'save-and-close' | 'transfer' | null;
  currentProject: Project | null;
  /** Timestamp (ms) set when the tab becomes hidden; used to detect long pauses
   *  and decide whether to restore the paramate session on return. */
  frozenAt: number | null;
  /** Marks that a paramate session snapshot is needed on the next visibility
   *  change or autosave tick. Set when any input changes. */
  sessionSnapshotNeeded: boolean;
  autoSaveDebounceTimer: number | null;
  /** Holds an unsaved session from a previous tab until the user chooses to
   *  restore or discard it. Cleared once the decision is made. */
  unsavedSessionProject: Project | null;
  /** Ergonomic wrapper around appData reads/writes — patch, defaults, revert,
   *  patchDefaults, subscribe. Prefer this over the deprecated members below. */
  readonly appData: AppDataAPI;
  /** @deprecated use `appData` instead — same object, renamed for consistency
   *  with `currentProjectData.appData`. */
  readonly projectData: AppDataAPI;
  /** @deprecated use `appData` instead. Live appData for the current project. */
  readonly customData: Record<string, any>;
  /** @deprecated use `appData.defaults` instead. Stored seed defaults — read-only. */
  readonly customDataDefaults: Record<string, any>;
  /** @deprecated use `appData.patch(patch, silent)` instead. Merge patch into
   *  appData and mark the project dirty. Set silent=true to skip dirty marking and history. */
  setCustomData(patch: Record<string, any>, silent?: boolean): void;
  /** @deprecated use `appData.defaults = defaults` instead. Store defaults and
   *  deep-merge missing keys into appData (existing values win at every level). */
  setCustomDataDefaults(defaults: Record<string, any>): void;
  /** @deprecated use `appData.revert()` instead. Wipe appData back to the stored defaults. */
  revertCustomData(): void;
  /** @deprecated use `appData.patchDefaults()` instead. Re-merge the stored
   *  defaults into the current appData (existing values win) — backfills any
   *  seed missing from a wholesale replace (e.g. an older saved project blob that
   *  predates a seed being added). */
  reapplyCustomDataDefaults(): void;
  /** @deprecated use `appData.subscribeAll(cb)` instead. Subscribe to appData
   *  changes. Returns an unsubscribe function. */
  customDataSubscribe(callback: (data: Record<string, any>) => void): () => void;
  /**
   * Register a hook run (awaited, in registration order) just before a project is
   * serialized — the place to flush the app's latest state into customData. The
   * hook is told why it's running (`{ reason }`): 'save' for a real save, 'autosave'
   * for the debounced in-memory capture, so an expensive flush can be limited to
   * real saves. Sync hooks are fine — the runner awaits either way. Returns an
   * opaque handle for removeBeforeSaveHook(); do NOT rely on indices.
   */
  addBeforeSaveHook(fn: BeforeSaveHook): BeforeSaveHookHandle;
  /** Remove a previously registered before-save hook by its handle. */
  removeBeforeSaveHook(handle: BeforeSaveHookHandle): void;
  /** Remove all before-save hooks. */
  clearBeforeSaveHooks(): void;
  /**
   * Run all before-save hooks in registration order (awaited). Hooks are isolated
   * from each other: one that throws — sync or async — is collected and the rest
   * still run. Never rejects; resolves with the failures, empty when all passed.
   */
  runBeforeSaveHooks(reason?: BeforeSaveReason): Promise<BeforeSaveHookFailure[]>;
  /** Register the app's version-keyed migrations (see data/projectManagement.ts
   *  for the MigrationMap shape). Keys are compared against the `app-version`
   *  attribute. Same as calling setMigrations() directly, just reachable off the
   *  app element without importing projectManagement.ts. */
  setMigrations(migrations: MigrationMap): void;
  /**
   * Deserialize an .asproject file (or any Blob containing a serialized
   * project — e.g. from a host app's own drop zone or file picker) and load
   * it as the current project. Rejects (with a user-facing notification, no
   * throw) files that fail to parse or that were saved by a different app.
   * Resolves false on failure, true once loaded.
   */
  loadProject(file: Blob): Promise<boolean>;
}

/** Internal project methods other mixins call. Separate from ProjectMixinMembers
 *  so the public surface above stays the app-facing API. */
export interface ProjectMixinInternals {
  _createAndSetNewProject(): void;
  _loadProject(project: Project): Promise<void>;
  _saveCurrentProject(name?: string): Promise<boolean>;
  _closeSaveProjectModal(): void;
  _handleSessionExpired(): void;
}

export const ProjectMixin = <T extends Constructor<HTMLElement & ModalMixinMembers & ErrorMixinMembers & InitQueueMixinMembers>>(Base: T) =>
  class ProjectMixin extends Base {
    isSaving: boolean = false;
    pendingAction: 'new-session' | 'home' | 'save-and-close' | 'transfer' | null = null;
    currentProject: Project | null = null;
    frozenAt: number | null = null;
    sessionSnapshotNeeded: boolean = false;
    autoSaveDebounceTimer: number | null = null;
    unsavedSessionProject: Project | null = null;

    _customDataDefaults: Record<string, any> = {};
    _appDataApi: AppDataAPI | null = null;

    /** AsApp's own members plus clearHistory, none of which this mixin can
     *  constrain its base on. One cast, read through a named type. */
    get _host(): ProjectHost { return this as unknown as ProjectHost; }

    // ------------------------------------------------------------------
    // Dirty tracking
    // ------------------------------------------------------------------

    _unsavedChanges: boolean = false;
    get unsavedChanges(): boolean { return this._unsavedChanges; }

    set unsavedChanges(value: boolean) {
      this._unsavedChanges = value;
      internalData.unsavedChanges = value;
    }

    /**
     * Gate that suppresses dirty-marking. Starts FALSE: a fresh app is held clean
     * through the init cycle so the configurator's startup configure/param-sync
     * can't dirty a brand-new session. Released exactly once when init finishes
     * (see _releaseInitTracking, driven by as-intrn-configurator-ready). New /
     * Restore re-close it for the duration of their own settle (markPristine).
     * While false, markDirty() is a no-op.
     */
    _dirtyTrackingReady = false;
    _settleTimer: number | null = null;
    _onSettleConfigured: (() => void) | null = null;
    /** Fallback so a configurator that never signals ready can't leave the app
     *  permanently un-dirtyable (see connectedCallback). */
    _initReleaseTimer: number | null = null;

    /**
     * Mark the session dirty in response to a genuine user edit. A no-op while
     * dirty-tracking is suppressed (the init cycle, or markPristine's New/Restore
     * settle window), so startup/reset param-sync events don't dirty a fresh
     * session. The single entry point for setting unsavedChanges = true.
     */
    markDirty(): void {
      if (!this._dirtyTrackingReady) return;
      this.unsavedChanges = true;
      this.sessionSnapshotNeeded = true;
    }

    /**
     * Release the one-time init suppression: init has finished, so clear any
     * startup settle dirt and begin tracking genuine edits. Unlike New/Restore,
     * cold boot triggers NO follow-up configure to wait on — so we must not wait
     * for a future after-configure (that "next configure" would be the user's own
     * first edit, which is exactly the bug this avoids). Anything an app does that
     * dirties *after* init is a real change; such an app is responsible for
     * clearing it itself (e.g. unsavedChanges = false). Idempotent.
     *
     * Not a real (#) private field: tests call this directly by name to drive
     * post-init dirty-tracking behavior without a real configurator.
     */
    _releaseInitTracking(): void {
      if (this._initReleaseTimer !== null) { clearTimeout(this._initReleaseTimer); this._initReleaseTimer = null; }
      this.unsavedChanges = false;
      this.sessionSnapshotNeeded = false;
      this._dirtyTrackingReady = true;
    }

    /**
     * Reset to a clean session and suppress dirty-tracking until it settles.
     * Called by New / Restore: each clears the session and then *itself* triggers
     * an async configure (as-reset-configurator / restoreSession) whose param-sync
     * would otherwise re-dirty the fresh session. So we clear and gate off
     * markDirty() until that triggered configure completes (one-shot
     * after-configure listener, with a timeout fallback). Cold boot does NOT use
     * this — it has no follow-up configure and releases directly via
     * _releaseInitTracking.
     */
    markPristine(): void {
      this._resumeDirtyTracking();          // cancel any in-flight settle from a prior call
      this._dirtyTrackingReady = false;
      this.unsavedChanges = false;
      this.sessionSnapshotNeeded = false;

      this._onSettleConfigured = () => this._resumeDirtyTracking();
      window.addEventListener(AS_CONFIGURATOR_EVENTS.PARAMATE_AFTER_CONFIGURE, this._onSettleConfigured, { once: true });
      this._settleTimer = window.setTimeout(() => this._resumeDirtyTracking(), 4000);
    }

    /** Resume dirty-tracking and tear down any pending settle listener/timer. */
    _resumeDirtyTracking(): void {
      if (this._settleTimer !== null) { clearTimeout(this._settleTimer); this._settleTimer = null; }
      if (this._onSettleConfigured) {
        window.removeEventListener(AS_CONFIGURATOR_EVENTS.PARAMATE_AFTER_CONFIGURE, this._onSettleConfigured);
        this._onSettleConfigured = null;
      }
      this._dirtyTrackingReady = true;
    }

    // ------------------------------------------------------------------
    // appData
    // ------------------------------------------------------------------

    /** @deprecated use `appData` instead. */
    get customData(): Record<string, any> {
      warnDeprecated('customData', 'appData');
      return currentProjectData.appData;
    }

    /** @deprecated use `appData.defaults` instead. */
    get customDataDefaults(): Record<string, any> {
      warnDeprecated('customDataDefaults', 'appData.defaults');
      return this._customDataDefaults;
    }

    // Shared implementations — both the deprecated flat methods below and
    // `appData` call these directly, so the deprecation warning only fires
    // on the old entry points.
    // Top-level merge, the same write `appData.patch` makes. It used to deep-merge
    // nested objects, which contradicted appData's top-level-key model and woke
    // subscribers for keys the patch never named.
    _doSetCustomData(patch: Record<string, any>, silent = false): void {
      currentProjectData.patchAppData(patch, silent);
    }

    // Cloned in and out, so the seeds can never be reached by a caller mutating
    // an appData array in place. lodash, not structuredClone: older hosts lack it.
    _doSetCustomDataDefaults(defaults: Record<string, any>): void {
      this._customDataDefaults = _cloneDeep(defaults);
      currentProjectData.setAppData(_merge({}, this._customDataDefaults, currentProjectData.appData), true);
    }

    _doRevertCustomData(): void {
      currentProjectData.setAppData(_cloneDeep(this._customDataDefaults), true);
    }

    _doReapplyCustomDataDefaults(): void {
      currentProjectData.setAppData(_merge({}, _cloneDeep(this._customDataDefaults), currentProjectData.appData), true);
    }

    get appData(): AppDataAPI {
      if (this._appDataApi) return this._appDataApi;
      const self = this;
      // Proxy over the live appData object itself — any key other than the
      // reserved method names below reads/writes straight through to appData,
      // so `appData` with no subfield behaves exactly like `currentProjectData.appData`.
      this._appDataApi = new Proxy(currentProjectData.appData, {
        get(target, prop, receiver) {
          switch (prop) {
            case 'patch':        return (patch: Record<string, any>, silent = false) => self._doSetCustomData(patch, silent);
            case 'set':          return (next: Record<string, any>, silent = false) => currentProjectData.setAppData(next, silent);
            case 'defaults':     return self._customDataDefaults;
            case 'revert':       return () => self._doRevertCustomData();
            case 'patchDefaults': return () => self._doReapplyCustomDataDefaults();
            case 'subscribe':    return appDataNs.subscribe;
            case 'subscribeAll': return (cb: (data: Record<string, any>) => void) => appDataNs.subscribeAll(cb);
            case 'derive':       return (spec: Record<string, DeriveAppDataSpec<any>>) => currentProjectData.derive(spec);
            case 'print':        return () => console.log({ ...currentProjectData.appData });
            default:             return Reflect.get(target, prop, receiver);
          }
        },
        set(target, prop, value) {
          if (prop === 'defaults') {
            self._doSetCustomDataDefaults(value);
            return true;
          }
          return Reflect.set(target, prop, value);
        },
      }) as AppDataAPI;
      return this._appDataApi;
    }

    /** @deprecated use `appData` instead — same object, renamed for consistency
     *  with `currentProjectData.appData`. */
    get projectData(): AppDataAPI {
      warnDeprecated('projectData', 'appData');
      return this.appData;
    }

    /** @deprecated use `appData.patch(patch, silent)` instead. */
    setCustomData(patch: Record<string, any>, silent = false): void {
      warnDeprecated('setCustomData', 'appData.patch');
      this._doSetCustomData(patch, silent);
    }

    /** @deprecated use `appData.defaults = defaults` instead. */
    setCustomDataDefaults(defaults: Record<string, any>): void {
      warnDeprecated('setCustomDataDefaults', 'appData.defaults =');
      this._doSetCustomDataDefaults(defaults);
    }

    /** @deprecated use `appData.revert()` instead. */
    revertCustomData(): void {
      warnDeprecated('revertCustomData', 'appData.revert');
      this._doRevertCustomData();
    }

    /** @deprecated use `appData.patchDefaults()` instead. */
    reapplyCustomDataDefaults(): void {
      warnDeprecated('reapplyCustomDataDefaults', 'appData.patchDefaults');
      this._doReapplyCustomDataDefaults();
    }

    /** @deprecated use `appData.subscribeAll(cb)` instead. */
    customDataSubscribe(callback: (data: Record<string, any>) => void): () => void {
      warnDeprecated('customDataSubscribe', 'appData.subscribeAll');
      return appDataNs.subscribeAll(callback);
    }

    setMigrations(migrations: MigrationMap): void {
      setMigrations(migrations);
    }

    // ------------------------------------------------------------------
    // Before-save hooks
    // ------------------------------------------------------------------

    // Insertion-ordered registry keyed by opaque handles (a Map preserves insertion
    // order), so hooks run in registration order and removal stays stable as hooks
    // are added/removed.
    _beforeSaveHooks: Map<BeforeSaveHookHandle, BeforeSaveHook> = new Map();

    addBeforeSaveHook(fn: BeforeSaveHook): BeforeSaveHookHandle {
      const handle: BeforeSaveHookHandle = Symbol('before-save-hook');
      this._beforeSaveHooks.set(handle, fn);
      return handle;
    }

    removeBeforeSaveHook(handle: BeforeSaveHookHandle): void {
      this._beforeSaveHooks.delete(handle);
    }

    clearBeforeSaveHooks(): void {
      this._beforeSaveHooks.clear();
    }

    async runBeforeSaveHooks(reason: BeforeSaveReason = 'save'): Promise<BeforeSaveHookFailure[]> {
      const failures: BeforeSaveHookFailure[] = [];
      for (const fn of this._beforeSaveHooks.values()) {
        // One hook per try/catch. Before this, the first throw cancelled every
        // hook registered after it AND aborted the caller's whole capture — one
        // app's bad hook could quietly stop another's state being flushed.
        // `await` inside the try covers sync throws and rejections alike, so
        // plain sync hooks need nothing special.
        try { await fn({ reason }); }
        catch (error) { failures.push({ fn, error }); }
      }
      return failures;
    }

    /**
     * Surface hooks that threw. A failed hook means the project is missing whatever
     * that hook was meant to flush. On the debounced autosave that's survivable —
     * the next capture tries again — so it stays a warning; on a real save the file
     * is written short while the user is told it saved, which is the case worth
     * escalating (_saveCurrentProject also says so in the UI). Either way it now
     * reaches the server log instead of only the console. The hook function is
     * printed with the error because hooks have no name of their own — devtools
     * links the printed function straight to its source.
     */
    _reportHookFailures(tag: string, reason: BeforeSaveReason, failures: BeforeSaveHookFailure[]): void {
      if (!failures.length) return;
      for (const { fn, error } of failures) {
        console.warn(`[${tag}] before-save hook failed:`, error, fn);
        this.logError('before-save-hook', error, reason === 'save' ? 'regular' : 'warning');
      }
      sendEv(this, 'as-before-save-hook-failed', { detail: { reason, failures } });
    }

    // ------------------------------------------------------------------
    // Capture and autosave
    // ------------------------------------------------------------------

    /**
     * Announce that the project state has been committed. Every save path ends
     * here — a real file save and the in-memory autosave capture both fire
     * `as-after-project-saved`, told apart by `detail.reason`. Autosave gets the
     * event too so a host that mirrors project state (a status line, its own copy
     * of customData) hears about it without polling; anything that acts on a
     * finished save must gate on isRealProjectSave.
     */
    _dispatchProjectSaved(reason: BeforeSaveReason): void {
      sendEv<ProjectSavedDetail>(this, 'as-after-project-saved', { detail: { reason } });
      //TODO:@REMOVE — deprecated mirror of as-after-project-saved. Fired second,
      // same detail.
      sendEv<ProjectSavedDetail>(this, 'as-project-saved', { bubbles: false, detail: { reason } });
    }

    /**
     * Flush the app's latest state onto `this.currentProject` ahead of
     * serialization/storage — the single collect point shared by every save path:
     *   1. run before-save hooks so apps can write their latest state into customData,
     *   2. capture the live paramate session (→ `currentProject.paramateSession`),
     *   3. snapshot `uiInputs` + `customData` onto `currentProject`.
     * Hook/session failures are reported with the caller's `tag` but never abort the
     * save. Returns the hook failures so a caller can tell the user its save came out
     * incomplete. The caller must have already verified `currentProject` is set.
     */
    async _captureProjectState(tag: string, reason: BeforeSaveReason): Promise<BeforeSaveHookFailure[]> {
      // Fires before the hooks run, so it's the last chance to change what gets
      // captured without registering a hook. Same detail as the after event.
      sendEv<ProjectSavedDetail>(this, 'as-before-project-saved', { detail: { reason } });
      // Let apps flush their latest state into customData before we read it. The
      // runner isolates hooks from each other and never rejects, so a broken hook
      // costs only its own contribution — not the rest of the capture.
      const hookFailures = await this.runBeforeSaveHooks(reason);
      this._reportHookFailures(tag, reason, hookFailures);

      const configuratorEl = asConfigurator();
      // Skip while a session reinit is swapping the live instance (see
      // isReinitializing on AsConfigurator) — a storeSession() landing mid-swap
      // can race the same token/configSession rotation window as any other caller.
      if (configuratorEl?.initialized && !configuratorEl.isReinitializing
          && configuratorEl.sessionId !== sessionCaptureBrokenFor) {
        try { await configuratorEl.storeSession(); }
        catch (err) {
          console.warn(`[${tag}] Could not capture paramate session — giving up on session capture for this session:`, err);
          sessionCaptureBrokenFor = configuratorEl.sessionId ?? null;
        }
      }

      this.currentProject!.uiInputs = { ...internalData.uiInputs };
      this.currentProject!.customData = { ...currentProjectData.appData };

      return hookFailures;
    }

    // Autosave to IDB is disabled to prevent untitled projects from piling up,
    // but we STILL run an in-memory capture so that `currentProject.paramateSession`
    // stays up-to-date. This is required to restore the session if the computer goes to sleep!
    _scheduleAutoSave(delayMs: number = AUTOSAVE_DEBOUNCE_MS): void {
      if (this.autoSaveDebounceTimer) clearTimeout(this.autoSaveDebounceTimer);
      this.autoSaveDebounceTimer = window.setTimeout(async () => {
        this.autoSaveDebounceTimer = null;
        if (!this.currentProject) return;
        // Never capture mid-operation. A configure, geometry build, or export in
        // flight means the app's own data and the params are still converging:
        // before-save hooks see a half-applied world (shadow-board's hook rebuilt
        // its board from meshes the app hadn't received yet, and threw), and
        // storeSession() races the same window. Wait for idle instead — busy keys
        // are ref-counted and always cleared by whoever set them, and the user
        // can't lose anything by us capturing a moment later.
        if (isAppBusy()) { this._scheduleAutoSave(AUTOSAVE_BUSY_RETRY_MS); return; }
        await this._captureProjectState('IN-MEMORY-AUTOSAVE', 'autosave');
        this.currentProject.metadata.updatedAt = new Date();
        // IDB save is skipped here.
        this.sessionSnapshotNeeded = false;
        // Note the unsavedChanges flag is deliberately left alone — nothing was
        // written to disk, so the project is still unsaved as far as the user
        // (and the exit guards) are concerned.
        this._dispatchProjectSaved('autosave');
      }, delayMs);
    }

    /**
     * Run a pending capture now rather than waiting out the debounce. Called when
     * the tab is hidden: the capture is what makes a session survive the machine
     * going to sleep, and at a 30s debounce a user who edits and then closes the
     * lid would otherwise lose those last edits. Does nothing when no capture is
     * pending, or when the app is mid-operation — there the debounce timer is
     * left running so the normal path still picks it up (see _scheduleAutoSave).
     */
    async _flushPendingAutoSave(): Promise<void> {
      if (!this.autoSaveDebounceTimer || !this.currentProject || isAppBusy()) return;
      clearTimeout(this.autoSaveDebounceTimer);
      this.autoSaveDebounceTimer = null;
      await this._captureProjectState('AUTOSAVE-FLUSH', 'autosave');
      this.currentProject.metadata.updatedAt = new Date();
      this.sessionSnapshotNeeded = false;
      this._dispatchProjectSaved('autosave');
    }

    // ------------------------------------------------------------------
    // Project CRUD
    // ------------------------------------------------------------------

    _createAndSetNewProject(): void {
      // Tell the app first, while the old project is still current — a listener
      // can read the outgoing state here (detail.project is the old one, or null
      // when there was none).
      sendEv(this, 'as-before-project-new', { detail: { project: this.currentProject } });

      // Start the session reinit FIRST. as-configurator reacts to this
      // synchronously by gating configure()/setParamValue()/... until the new
      // session is live. Everything below can trigger app-side reactions
      // (customData subscribers, input resets) that call configure() right away —
      // with the gate already up those calls queue and land in the fresh session
      // with the reverted values. Fired any later, they went out against the OLD
      // session: harmless while it was still alive (header New), but a 404 "route
      // not found" when it had expired (Start New Session from the expiry modal).
      sendEv(window, 'as-reset-configurator');

      // Wipe back to the app's registered seed defaults, not to {} — a bare wholesale
      // clear leaves every seed undefined until each app's own New/reset listener
      // happens to write it explicitly, and any read (or subscribeKey side effect)
      // that fires in between crashes on the missing default.
      this._doRevertCustomData();
      this.currentProject = createNewProject(this._host.appId);
      internalData.currentProject = this.currentProject;
      // Settle clean: the reinit above ends in an async configure whose param-sync
      // re-dirties after this turn, so a one-shot clear wouldn't stick.
      this.markPristine();
      internalData.uiInputs = {};

      // Reset all inputs to their default states
      sendEv(window, 'as-inputs-reset', { detail: { silenceChange: true } });

      // Reset step selector to initial value (bypassing any locks).
      // silenceChange: true prevents app step-change handlers from firing a configure
      // cycle that would push spurious history entries before the stack is cleared.
      sendEv(window, 'as-force-step', { detail: { stepIndex: 0, silenceChange: true } });

      // Then, notify that a new project has been created
      sendEv(this, 'as-after-project-new', { detail: { project: this.currentProject } });
      //TODO:@REMOVE — deprecated mirror of as-after-project-new. Fired second,
      // same detail.
      sendEv(this, 'as-project-new', { detail: { project: this.currentProject } });

      sendEv(this, 'as-notify', {
        bubbles: false,
        detail: { defaultText: 'New project created', dictKey: 'notifications.project.new', type: 'info' }
      });

      // Defer so any async configurator reset work (as-reset-configurator) has settled
      setTimeout(() => this._host.clearHistory(), 0);
    }

    async _loadProject(project: Project): Promise<void> {
      sendEv(window, 'as-app-busy-request', { detail: { key: 'load-project', busy: true } });
      _setSysData({ projectLoading: true });
      // Bookend for as-after-project-loaded below. Carries the project as it
      // arrived — migrations haven't run yet, so this is not necessarily what the
      // after event will report.
      sendEv<ProjectLoadDetail>(this, 'as-before-project-loaded', { detail: { project } });
      try {
        // Reset step selector to initial value (bypassing any locks) before we start restoring.
        // This prevents step-change side effects (like configure calls) from interrupting
        // the asynchronous model reloading during restoreSession.
        sendEv(window, 'as-force-step', {
          detail: { stepIndex: 0, silenceChange: !!project.paramateSession },
        });

        const configuratorEl = asConfigurator();

        let currentSessionBlob: Blob | undefined;
        if (configuratorEl?.initialized && project.paramateSession) {
          try { currentSessionBlob = await configuratorEl.getSessionBlob(); } catch { /* skip merge */ }
        }
        try {
          project = await applyMigrations(project, this.getAttribute('app-version') ?? undefined, currentSessionBlob);
        } catch (err) {
          console.warn('[LOAD] Session migration failed — loading without session merge:', err);
          project = { ...project, paramateSession: undefined };
        }

        this.currentProject = project;
        internalData.currentProject = project;
        // currentProjectData.uiInputs is an accessor onto this same field, so one
        // write is the whole job.
        internalData.uiInputs = { ...project.uiInputs };
        // Merge defaults into the incoming blob BEFORE the single setAppData call
        // below — a saved project's blob may predate a seed the app has since
        // registered a default for, same reasoning as revertCustomData() on the New
        // path. Merging first (rather than set-then-reapplyCustomDataDefaults) avoids
        // a transient notification where subscribers see that seed as genuinely
        // undefined: setAppData notifies per-key subscribers synchronously, so a
        // separate backfill call one line later is one synchronous notification too
        // late for anything already reacting to the first.
        currentProjectData.setAppData(_merge({}, _cloneDeep(this._customDataDefaults), project.customData ?? {}), true);

        if (project.paramateSession && configuratorEl) {
          const restored = await new Promise<boolean>(resolve => {
            const done = (ok: boolean) => {
              window.removeEventListener('paramate-configurator-after-restore', onRestored);
              window.removeEventListener('as-session-restore-failed', onFailed);
              resolve(ok);
            };
            const onRestored = () => done(true);
            const onFailed = () => done(false);
            window.addEventListener('paramate-configurator-after-restore', onRestored);
            window.addEventListener('as-session-restore-failed', onFailed);
            configuratorEl.restoreSession(project.paramateSession!);
            devlog('[LOAD] Restoring paramate session, waiting for after-restore…');
          });
          devlog('[LOAD] Paramate session restore complete');
          // Put back the edits that were pending at save time: the restore brought
          // the session to its saved baseline, these go on top as dirty again —
          // same state the project was saved in. See Project.pendingParams.
          // Checked here as well as at capture time so an app that switches
          // options.pendingParams off also ignores files saved before it did.
          if (restored && project.pendingParams && configuratorEl.options.pendingParams !== false) {
            configuratorEl.applyPendingParams(project.pendingParams);
          }
        }

        if (Object.keys(project.uiInputs).length > 0) {
          sendEv(window, 'as-restore-input-values', {
            detail: { inputs: project.uiInputs, silent: !!project.paramateSession },
          });
        }

        // Internal: inputs and CAD state are back. Apps should use
        // as-after-project-loaded below instead.
        sendEv(this, 'as-intrn-session-restored', { detail: { customData: currentProjectData.appData } });
        //TODO:@REMOVE — deprecated unprefixed name, kept for apps still listening.
        sendEv(this, 'session-restored', { detail: { customData: currentProjectData.appData } });

        // restored
        sendEv<ProjectLoadDetail>(this, 'as-after-project-loaded', { detail: { project } });
        //TODO:@REMOVE — deprecated mirror of as-after-project-loaded. Fired second
        // and with the same detail, so a listener on either sees the same state.
        sendEv<ProjectLoadDetail>(this, 'as-project-loaded', { detail: { project } });

        // Settle clean: a restore triggers an async configure that re-dirties after
        // this turn, so clear through the settle window rather than once.
        this.markPristine();
        this._host.clearHistory();
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'Loaded project: {0}', dictKey: 'notifications.project.loaded', vars: [project.metadata.name], type: 'info' }
        });

      } finally {
        sendEv(window, 'as-app-busy-request', { detail: { key: 'load-project', busy: false } });
        // Belt and braces with the as-after-project-loaded listener in
        // sysDataStore: that event doesn't fire when the load throws, and a
        // projectLoading stuck true would outlast the app.
        _setSysData({ projectLoading: false });
      }
    }

    /**
     * Deserialize an .asproject file/blob and load it as the current project.
     * For File objects arriving outside the built-in restore-session-file flow
     * (e.g. a host app's own drop zone or file picker). A file picked from disk
     * has no guarantee it belongs to this app, hence the appId check.
     * Returns false (after surfacing a notification) on parse failure or app-id
     * mismatch; true once the project has been loaded.
     */
    async loadProject(file: Blob): Promise<boolean> {
      let project: Project;
      try {
        project = await deserializeProject(file);
      } catch (err) {
        this.logError('load-project-file', err, 'regular');
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'Failed to load project file', dictKey: 'notifications.project.loadFailed', type: 'error' }
        });
        return false;
      }

      if (project.metadata.appId !== this._host.appId) {
        console.warn(`[LOAD] Refusing to load project for app "${project.metadata.appId}" into app "${this._host.appId}"`);
        sendEv(window, 'as-session-restore-failed', {
          detail: { reason: 'app-id-mismatch', expectedAppId: project.metadata.appId }
        });
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'This .asproject file is meant for the {0} app.', dictKey: 'notifications.project.wrongApp', vars: [project.metadata.appId], type: 'error', persist: true }
        });
        return false;
      }

      await this._loadProject(project);
      return true;
    }

    /**
     * Save the current project via the regular file-save method (saveFileAs).
     * Returns true only when a file was actually written; false when there's nothing
     * to save, a save is already running, or the user cancelled the save dialog — so
     * callers can gate "save-and-close / save-and-home" on a real save and never
     * proceed (and lose work) on a cancel.
     */
    async _saveCurrentProject(name?: string): Promise<boolean> {
      devlog('[SAVE] saveCurrentProject: entry — currentProject:', !!this.currentProject, '| isSaving:', this.isSaving, '| appEnv:', internalData.appEnv ?? '(none)');
      if (!this.currentProject) { devlog('[SAVE] saveCurrentProject: no currentProject, abort'); return false; }
      if (this.isSaving) { devlog('[SAVE] Already saving, skipping duplicate call'); return false; }
      this.isSaving = true;
      _setSysData({ projectSaving: true });   // as-before-project-saved fires inside _captureProjectState below
      try {
        if (name) this.currentProject.metadata.name = name;

        const hookFailures = await this._captureProjectState('SAVE', 'save');

        const blob = await serializeProject(this.currentProject);
        const _appName = (asHeader()?.getAttribute('app-title') ?? this._host.appId).replace(/\s+/g, '');
        const filename = `${this.currentProject.metadata.name}.${_appName}.asproject`;

        const result = await saveFileAs(blob, filename, {
          title: 'Save Project',
          fileExtension: 'asproject',
          fileExtensionName: 'App Suite Project',
        });
        if (!result.success) {
          devlog('[SAVE] Save cancelled by user');
          // Symmetric to as-project-saved below — lets listeners that only learn
          // about a save's outcome through events (e.g. as-modal-grabcad-success's
          // saving-spinner state) react to a cancel immediately instead of relying
          // on their own blind recovery timers.
          sendEv(this, 'as-project-save-cancelled', { bubbles: false });
          return false;
        }
        devlog('[SAVE] Written:', filename, result.filepath ?? '(via download)');

        this.currentProject = markProjectSaved(this.currentProject);
        // IDB persistence disabled — file download is the only save path for now.
        internalData.currentProject = this.currentProject;
        this.unsavedChanges = false;

        this._dispatchProjectSaved('save');
        // The file is on disk either way, but a hook that threw means it's missing
        // whatever that hook was meant to flush. Reporting a clean save there is how
        // a user ends up reopening a project with state silently gone.
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: hookFailures.length
            ? {
                defaultText: 'Project saved: {0}, but some app state could not be captured',
                dictKey: 'notifications.project.savedIncomplete',
                vars: [this.currentProject.metadata.name], type: 'warning', persist: true,
              }
            : {
                defaultText: 'Project saved: {0}',
                dictKey: 'notifications.project.saved',
                vars: [this.currentProject.metadata.name], type: 'success',
              }
        });
        return true;
      } finally {
        this.isSaving = false;
        _setSysData({ projectSaving: false });
      }
    }

    // ------------------------------------------------------------------
    // Modal helpers
    // ------------------------------------------------------------------

    _openNewProjectWarningModal(): void {
      this.openModal('unsaved-warning', 'as-modal-unsaved-warning', { mode: 'new' });
      this.modalOverlay?.querySelector('#unsaved-warning')?.removeAttribute('saved');
    }

    _openSaveProjectModal(): void {
      const saveModal = this.modalOverlay?.querySelector('#save-project') as AsModalSaveProject | null;
      if (saveModal) {
        if (this.currentProject?.metadata.name && this.currentProject.metadata.name !== 'Untitled') {
          saveModal.setValue(this.currentProject.metadata.name);
        } else {
          saveModal.reset();
        }
      }
      this.openModal('save-project', 'as-modal-save-project');
      setTimeout(() => {
        (this.modalOverlay?.querySelector('#save-project') as AsModalSaveProject | null)?.focusInput();
      }, 100);
    }

    _closeSaveProjectModal(): void {
      if (this.modalOverlay?.hasAttribute('show')) this.modalOverlay.close();
    }

    // ------------------------------------------------------------------
    // Session event handlers
    // ------------------------------------------------------------------

    _handleNewSession(): void {
      if (this.unsavedChanges) {
        this.pendingAction = 'new-session';
        this._openNewProjectWarningModal();
      } else {
        this._createAndSetNewProject();
      }
    }

    _handleSaveSession(): void {
      devlog('[SAVE] handleSaveSession: pendingAction:', this.pendingAction);
      devlog('[SAVE] handleSaveSession: currentProject:', !!this.currentProject, '| name:', this.currentProject?.metadata.name ?? '(none)');

      if (!this.currentProject || this.currentProject.metadata.name === 'Untitled') {
        devlog('[SAVE] handleSaveSession: opening save-project modal (name required)');
        this._openSaveProjectModal();
      } else if (this.currentProject) {
        devlog('[SAVE] handleSaveSession: calling saveCurrentProject');
        this._saveCurrentProject();
      } else {
        devlog('[SAVE] handleSaveSession: no currentProject, nothing to save');
      }
    }

    async _handleRestoreSessionFile(event: Event): Promise<void> {
      const { blob } = (event as CustomEvent).detail ?? {};
      if (!blob) return;
      try {
        const project = await deserializeProject(blob);
        await this._loadProject(project);
      } catch (err) {
        this.logError('restore-file', err, 'regular');
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'Failed to load project file', dictKey: 'notifications.project.loadFailed', type: 'error' }
        });
      }
    }

    // ------------------------------------------------------------------
    // Sleep / visibility / expiry
    // ------------------------------------------------------------------

    async _handleVisibilityChange(): Promise<void> {
      const configuratorEl = asConfigurator();

      if (document.visibilityState === 'hidden') {
        this.frozenAt = Date.now();
        if (!document.title.includes('(paused)')) document.title += ' (paused)';

        // Take the outstanding capture before the tab goes quiet — a hidden tab
        // is the last moment we get if the machine then sleeps past the session
        // timeout, and the capture is what the expiry flow restores from.
        //
        // Not in GrabCAD. There "hidden" is nearly always the window being
        // destroyed — closing the plugin window, or quitting GrabCAD Print — and
        // the capture fetches the session zip from Paramate. A request that starts
        // while the webContents is going away reaches the host's plugin request
        // filter with no owning webContents, and the filter feeds that straight
        // into webContents.fromId(), which throws and takes all of GrabCAD Print
        // down with it. Nothing was gained by trying, either: a request begun that
        // late is cancelled along with the window, so the capture never landed.
        // The debounce timer is left running, so a window that was merely hidden
        // still gets its capture the normal way once it comes back.
        if (internalData.appEnv !== 'grabcad') await this._flushPendingAutoSave();

        if (!configuratorEl?.initialized) return;
        configuratorEl.pauseSessionHeartbeat();

        // Autosave disabled — skip IDB snapshot on tab hide.
      } else {
        document.title = document.title.replace(' (paused)', '');
        const elapsed = this.frozenAt ? Date.now() - this.frozenAt : 0;
        this.frozenAt = null;

        const expired = elapsed > SESSION_TIMEOUT_MS;
        if (expired && configuratorEl?.initialized) {
          this._handleSessionExpired();
        }

        // Sync the interval baseline to prevent it from double-triggering
        // if the tab was suspended.
        lastSleepTick = Date.now();

        // Don't restart the keepalive when the session just expired — it would ping
        // the dead session id and 404. The expiry flow restarts it on the new session.
        if (!expired && configuratorEl?.initialized) configuratorEl.resetSessionTimout();
      }
    }

    _initSleepDetection(): void {
      if (sleepDetectionInterval) return;
      lastSleepTick = Date.now();
      sleepDetectionInterval = window.setInterval(() => {
        const now = Date.now();
        const elapsed = now - lastSleepTick;
        lastSleepTick = now;

        if (elapsed > SESSION_TIMEOUT_MS) {
          this._handleSessionExpired();
        }
      }, 10000);
    }

    _teardownSleepDetection(): void {
      if (sleepDetectionInterval) {
        clearInterval(sleepDetectionInterval);
        sleepDetectionInterval = null;
      }
    }

    _handleSessionExpired(): void {
      const configuratorEl = asConfigurator();
      if (!configuratorEl?.initialized) return;

      // The server session is dead — stop the keepalive so nothing pings it (a 404
      // would surface as an error modal over the expiry flow). Restarted against
      // the fresh session by the reinit that Start New Session triggers.
      configuratorEl.pauseSessionHeartbeat();
      if (this.modalOverlay?.visibleChildId === 'expired-session') return;

      // No restore offer: expired sessions always route to a fresh project. The
      // restore path (refreshSession + loadProject of the captured blob) was too
      // unreliable to ship, so the modal's only action is Start New Session.
      this.openModal('expired-session', 'as-modal-expired-session');
    }

    /**
     * Reopen the unsaved session as-modal-restore-session offered. Queued
     * behind init because a load needs the configurator up. Nothing opens that
     * panel today: see the recovery note in connectedCallback.
     */
    _restoreUnsavedSession(): void {
      const project = this.unsavedSessionProject;
      if (!project) return;
      this.unsavedSessionProject = null;
      this.addToPostInitQueue(() => this._loadProject(project));
    }

    /** Counterpart to _restoreUnsavedSession: drop every unsaved project this
     *  app has in IDB, so the offer isn't made again. */
    _discardUnsavedSessions(): void {
      if (!this.unsavedSessionProject) return;
      this.unsavedSessionProject = null;
      getProjectsForApp(this._host.appId).then(projects => {
        projects.filter(p => !p.metadata.savedAt).forEach(p => deleteProject(p.metadata.id));
      });
    }

    // ------------------------------------------------------------------
    // Lifecycle
    // ------------------------------------------------------------------

    _projectSelfListeners: Record<string, EventListener> = {};
    _projectWindowListeners: Record<string, EventListener> = {};
    _projectDocumentListeners: Record<string, EventListener> = {};

    async connectedCallback() {
      await super.connectedCallback?.();

      this.currentProject = createNewProject(this._host.appId);
      internalData.currentProject = this.currentProject;

      // IDB-based unsaved session recovery disabled alongside autosave.

      this._projectSelfListeners = {
        'as-new-session':       () => this._handleNewSession(),
        'as-save-session':      () => this._handleSaveSession(),
        //TODO:@REMOVE — unprefixed names, deprecated in 0.16.109. Apps still
        // dispatch these on <as-app> themselves.
        'new-session':       () => this._handleNewSession(),
        'save-session':      () => this._handleSaveSession(),
        'as-restore-session-file': (e) => this._handleRestoreSessionFile(e),
        //TODO:@REMOVE — deprecated unprefixed name.
        'restore-session-file': (e) => this._handleRestoreSessionFile(e),
        'as-input-change': () => {
          this.markDirty();
          this._scheduleAutoSave();
        },
        // Autosave dispatches this event as well (detail.reason 'autosave'),
        // but it writes no file: treating that tick as a save would clear the
        // unsaved-changes flag on work that only exists in memory, and would
        // close the app mid-save-and-close. Only a real save counts here.
        'as-after-project-saved': (e) => {
          if (!isRealProjectSave(e)) return;
          this.unsavedChanges = false;
          if (this.pendingAction === 'save-and-close') {
            this.pendingAction = null;
            this.modalOverlay?.close();
            sendEv(window, 'as-close-app');
          }
        },
        // Cancel counterpart to as-after-project-saved above. Only 'save-and-close'
        // has no other cancel path (new-session/home/transfer all clear
        // pendingAction via cancel-save-project or as-intrn-cancel-exit-app,
        // since they always route through a modal the user can dismiss) — a
        // cancelled GrabCAD native save dialog has no modal of ours to cancel,
        // so without this pendingAction was left stuck at 'save-and-close'.
        'as-project-save-cancelled': () => {
          if (this.pendingAction === 'save-and-close') this.pendingAction = null;
        },
        // The session panels run their own buttons and send these intents up
        // (see components/modals/modalPanel.ts). They close themselves; what
        // is left is the project work, which is ours.
        'as-intrn-discard-expired-session': () => this._createAndSetNewProject(),
        'as-intrn-restore-unsaved-session': () => this._restoreUnsavedSession(),
        'as-intrn-discard-unsaved-session': () => this._discardUnsavedSessions(),
      } as Record<string, EventListener>;

      this._projectWindowListeners = {
        'as-project-change': () => {
          this.markDirty();
          this._scheduleAutoSave();
        },
        // Cold boot: the session is held clean through init (dirtyTrackingReady
        // starts false). Once the configurator signals ready, release tracking so
        // a fresh session doesn't start "unsaved" and the user's first real edit
        // is tracked. No settle-window past ready — see _releaseInitTracking.
        'as-intrn-configurator-ready': () => this._releaseInitTracking(),
        // The configurator's keepalive hit a dead session (machine slept past the
        // session TTL with the tab visible, so no visibilitychange fired) — route
        // it into the same expired-session flow as sleep detection.
        'as-session-lost': () => this._handleSessionExpired(),
      } as Record<string, EventListener>;

      this._projectDocumentListeners = {
        'visibilitychange': () => this._handleVisibilityChange(),
      } as Record<string, EventListener>;

      registerListeners(this, this._projectSelfListeners);
      registerListeners(window, this._projectWindowListeners);
      registerListeners(document, this._projectDocumentListeners);

      // Drive the one-time init release. A configurator app stays held until its
      // as-intrn-configurator-ready fires; an app with no configurator has no init
      // cycle, so enable tracking now. The timer is a safety net for a configurator
      // that never reports ready (e.g. init failure) so the app can't get stuck
      // permanently un-dirtyable.
      if (asConfigurator()) {
        this._initReleaseTimer = window.setTimeout(() => this._releaseInitTracking(), INIT_RELEASE_FALLBACK_MS);
      } else {
        this._releaseInitTracking();
      }

      // IDB project list disabled.

      this._initSleepDetection();
    }

    disconnectedCallback() {
      if (this._initReleaseTimer !== null) { clearTimeout(this._initReleaseTimer); this._initReleaseTimer = null; }
      this._resumeDirtyTracking();
      unregisterListeners(this, this._projectSelfListeners);
      unregisterListeners(window, this._projectWindowListeners);
      unregisterListeners(document, this._projectDocumentListeners);
      this._teardownSleepDetection();
      super.disconnectedCallback?.();
    }
  };
